import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" />
            <h1 className="font-heading font-black text-3xl">Terms of Use</h1>
          </div>
          <p className="text-white/80 mt-2">Effective Date: January 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-8 md:p-12">
          <p className="text-slate-600 leading-relaxed mb-8">
            These Terms of Use ("Terms") govern your use of ClearPath Testing Solutions (the "Service"). By using the Service, you agree to these Terms.
          </p>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">1) Who We Are</h2>
            <p className="text-slate-600">
              ClearPath Testing Solutions / Clear Path Testing Solutions LLC ("we," "us," "our") provides test preparation tools, practice, explanations, and progress tracking.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">2) Eligibility</h2>
            <p className="text-slate-600">
              You must be at least 13 years old to use the Service (or have parent/guardian or school supervision if under 13).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">3) Accounts</h2>
            <p className="text-slate-600 mb-3">You are responsible for:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Keeping your login secure</li>
              <li>All activity under your account</li>
              <li>Providing accurate account information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">4) Educational Use / No Guarantees</h2>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-slate-700 font-medium">
                The Service provides learning support and practice. <strong>We do not guarantee score increases, admissions, scholarships, job placement, or specific outcomes.</strong>
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">5) Acceptable Use</h2>
            <p className="text-slate-600 mb-3">You agree not to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Cheat, exploit, or attempt to break the Service</li>
              <li>Copy, scrape, reverse engineer, or resell content</li>
              <li>Upload harmful code, spam, or abusive content</li>
              <li>Use the Service for unlawful activity</li>
            </ul>
            <p className="text-slate-600 mt-3">
              We may suspend accounts for violations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">6) Subscriptions</h2>
            <p className="text-slate-600 mb-3">If Premium is offered:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Pricing and billing terms will be shown at purchase</li>
              <li>Subscriptions renew automatically unless canceled</li>
              <li>Refunds are handled according to our posted policy and/or the payment processor rules</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">7) Intellectual Property</h2>
            <p className="text-slate-600">
              All content, questions, explanations, branding, and software are owned by us or our licensors. You receive a personal, non-transferable license to use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">8) Third-Party Services</h2>
            <p className="text-slate-600">
              The Service may include links or integrations with third-party services. We are not responsible for their content or policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">9) Disclaimer</h2>
            <p className="text-slate-600">
              The Service is provided "as is" without warranties of any kind, to the maximum extent allowed by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">10) Limitation of Liability</h2>
            <p className="text-slate-600">
              To the maximum extent allowed by law, we are not liable for indirect, incidental, special, or consequential damages.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">11) Governing Law</h2>
            <p className="text-slate-600">
              These Terms are governed by the laws of the State of Texas, without regard to conflict of law rules.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">12) Contact</h2>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-slate-700">
                Questions about these Terms: <a href="mailto:support@clearpathtestprep.com" className="text-indigo-600 hover:underline">support@clearpathtestprep.com</a>
              </p>
              <p className="text-slate-600 mt-2">
                Clear Path Testing Solutions LLC<br />
                Texas, United States
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default Terms;
